import { CommonModule } from '@angular/common';
import { Component, ViewChild, signal, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatChipsModule } from '@angular/material/chips';
import { BoardMemberOccupation } from '../../../models/boardMemberOccupation';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { DraggableListComponent, DragEvent } from '../../shared/draggable-list/draggable-list.component';
import { LutService } from '../../../services/lut.service';
import { LookupDataService } from '../../../services/lookupDataService.service';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { HotToastService } from '@ngxpert/hot-toast';
import { forkJoin } from 'rxjs';
import { Helpers } from '../../../utilities/helpers';

@Component({
  selector: 'app-board-member-occupation-list',
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    MatInputModule,
    MatFormFieldModule,
    FormsModule,
    MatListModule,
    MatChipsModule,
    DraggableListComponent,
    NgxSpinnerModule
  ],
  templateUrl: './board-member-occupation-list.component.html',
  styleUrl: './board-member-occupation-list.component.scss'
})
export class BoardMemberOccupationListComponent implements OnInit {
  @ViewChild(DraggableListComponent<BoardMemberOccupation>) draggableList!: DraggableListComponent<BoardMemberOccupation>;
  items = signal<BoardMemberOccupation[]>([]);

  constructor(
    private confirmationDialogService: ConfirmationDialogService,
    private lutService: LutService,
    private lookupDataService: LookupDataService,
    private spinnerService: NgxSpinnerService,
    private toast: HotToastService
  ) {}

  ngOnInit(): void {
    this.loadBoardMemberOccupations();
  }

  loadBoardMemberOccupations(): void {
    this.spinnerService.show();
    this.lutService.getBoardMemberOccupations().subscribe({
      next: (data) => {
        // Sort by order property
        const sortedData = this.sortByOrder(data);
        this.items.set(sortedData);
        this.spinnerService.hide();
      },
      error: (error) => {
        console.error('Error loading board member occupations:', error);
        this.spinnerService.hide();
        this.toast.error('Failed to load board member occupations');
      }
    });
  }

  private sortByOrder(items: BoardMemberOccupation[]): BoardMemberOccupation[] {
    return [...items].sort((a, b) => a.order - b.order);
  }

  onCreateBoardMemberOccupation(): void {
    this.draggableList.startAdd();
  }

  onItemsReordered(reorderedItems: BoardMemberOccupation[]): void {
    // Store original items for rollback
    const originalItems = this.items();

    // Update order property for all items based on their new position
    const updatedItems = reorderedItems.map((item, index) => ({
      ...item,
      order: index + 1
    }));

    // 1. Optimistically update local state
    this.items.set(updatedItems);

    // 2. Call backend API to update all items
    const updateRequests = updatedItems.map(item =>
      this.lutService.updateBoardMemberOccupation(item.id, item)
    );

    this.spinnerService.show();
    forkJoin(updateRequests).subscribe({
      next: (responses) => {
        this.spinnerService.hide();
        this.toast.success('Order updated successfully');
        // Update cache so other components get updated data
        this.lookupDataService.updateTableData('boardMemberOccupations', this.items());
        // 3. Update with server responses
        this.items.set(this.sortByOrder(responses));
      },
      error: (error) => {
        console.error('Error reordering board member occupations:', error);
        this.spinnerService.hide();
        // 4. On error: Rollback to original state
        this.items.set(originalItems);
        this.toast.error(`<strong>Failed to update Order:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }

  onEditItem(item: BoardMemberOccupation): void {
  }

  onDeleteItem(item: BoardMemberOccupation): void {
    this.confirmationDialogService.openDeleteConfirmationDialog(item.name)
      .subscribe(result => {
        if (result) {
          // Store original list for rollback in case of error
          const originalItems = this.items();

          // 1. Optimistically remove from local state immediately
          this.items.update(items => items.filter(i => i.id !== item.id));

          // 2. Call backend API
          this.spinnerService.show();
          this.lutService.deleteBoardMemberOccupation(item.id).subscribe({
            next: () => {
              this.spinnerService.hide();
              this.toast.success('Board member occupation deleted successfully');
              // Update cache so other components get updated data
              this.lookupDataService.updateTableData('boardMemberOccupations', this.items());
              // 3. On success: Already removed from UI, nothing more to do
            },
            error: (error) => {
              console.error('Error deleting board member occupation:', error);
              this.spinnerService.hide();
              // 4. On error: Restore original list
              this.items.set(originalItems);
              this.toast.error(`<strong>Failed to delete Board Member Occupation:</strong></br></br> ${Helpers.parseApiError(error)}`);
            }
          });
        }
      });
  }

  onDragStarted(event: DragEvent<BoardMemberOccupation>): void {
  }

  onDragMoved(event: DragEvent<BoardMemberOccupation>): void {
    // This will log a lot during drag
  }

  onDragEnded(event: DragEvent<BoardMemberOccupation>): void {
  }

  onItemSave(event: {item: BoardMemberOccupation, newValue: string}): void {
    // Store original item for rollback in case of error
    const originalItem = { ...event.item };

    // Prepare updated item
    const updatedItem: BoardMemberOccupation = { ...event.item, name: event.newValue };

    // 1. Optimistically update local state immediately
    this.items.update(items =>
      items.map(item => item.id === event.item.id ? updatedItem : item)
    );

    // 2. Call backend API
    this.spinnerService.show();
    this.lutService.updateBoardMemberOccupation(event.item.id, updatedItem).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('Board member occupation updated successfully');
        // Update cache so other components get updated data
        this.lookupDataService.updateTableData('boardMemberOccupations', this.items());
        // 3. On success: Update with server response
        this.items.update(items =>
          items.map(item => item.id === event.item.id ? response : item)
        );
      },
      error: (error) => {
        console.error('Error updating board member occupation:', error);
        this.spinnerService.hide();
        // 4. On error: Rollback to original state
        this.items.update(items =>
          items.map(item => item.id === event.item.id ? originalItem : item)
        );
        this.toast.error(`<strong>Failed to update Board Member Occupation:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }

  onItemAdd(event: {[key: string]: any}): void {
    // Generate temporary ID for optimistic update
    const tempId = crypto.randomUUID();

    // Get the highest order number and add 1 for the new item
    const maxOrder = this.items().reduce((max, item) => Math.max(max, item.order), 0);

    const newBoardMemberOccupation: BoardMemberOccupation = {
      id: tempId,
      name: event['name'],
      order: maxOrder + 1,
      systemReserved: false
    };

    // 1. Optimistically add to local state immediately
    this.items.update(items => [...items, newBoardMemberOccupation]);

    // 2. Call backend API
    this.spinnerService.show();
    this.lutService.createBoardMemberOccupation({ name: event['name'], order: maxOrder + 1, systemReserved: false }).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('Board member occupation created successfully');
        // Update cache so other components get updated data
        this.lookupDataService.updateTableData('boardMemberOccupations', this.items());
        // 3. On success: Replace temp item with server response
        this.items.update(items =>
          items.map(item => item.id === tempId ? response : item)
        );
      },
      error: (error) => {
        console.error('Error creating board member occupation:', error);
        this.spinnerService.hide();
        // 4. On error: Remove the temp item
        this.items.update(items =>
          items.filter(item => item.id !== tempId)
        );
        this.toast.error(`<strong>Failed to create Board Member Occupation:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }

}
